import urls from '@/navigation/urls'
import userAtom from '@/pages/User/user.atom'
import useOrganizationUsers from '@/pages/Users/hooks/useOrganizationUsers'
import { IOption } from '@/types/global'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import usersUtils from '@/pages/Users/users.utils'

export type TViewingMode = 'list' | 'report'

const useMyOrganizationUsersCtrl = () => {
  const userState = useRecoilValue(userAtom)
  const navigate = useNavigate()

  const [viewingMode, setViewingMode] = useState<TViewingMode>('report')

  const [createUserSidePageOpen, setCreateUserSidePageOpen] = useState(false)
  const [positionFilter, setPositionFilter] = useState<string[]>([])
  const [activeFilter, setActiveFilter] = useState<number | null>(null)
  const { loading, users } = useOrganizationUsers(userState.organizationId)

  useEffect(() => {
    if (!userState.organizationId) navigate(urls.main)
  }, [userState.organizationId])

  const positionOptions: IOption<string>[] = useMemo(() => {
    const positionSet = new Set(users.map((u) => usersUtils.normalizePosition(u.position)).filter(Boolean))
    return [...positionSet].map((position) => ({
      title: position,
      value: position,
    }))
  }, [users])

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const byPosition = !positionFilter.length || positionFilter.includes(usersUtils.normalizePosition(u.position))
      const byActive = activeFilter == null || u.active === Boolean(activeFilter)
      return byPosition && byActive
    })
  }, [users, positionFilter, activeFilter])

  return {
    data: {
      organizationId: userState.organizationId,
      positionOptions,
      users: filteredUsers,
      usersLoading: loading,
      viewingMode,
      createUserSidePageOpen,
      positionFilter,
      activeFilter,
    },
    handlers: {
      switchViewingMode: () => setViewingMode((prev) => (prev === 'list' ? 'report' : 'list')),
      handlePositionFilterChange: (value: string[]) => setPositionFilter(value),
      handleActiveFilterChange: (value: number | null) => setActiveFilter(value),
      handleCreateUserSidePageOpen: (value: boolean) => setCreateUserSidePageOpen(value),
    },
  }
}

export default useMyOrganizationUsersCtrl
